import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plane, AlertTriangle, Plus, Ticket, Sparkles, Check } from "lucide-react";
import { tripPlanApi, itineraryApi } from "@/lib/api";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import type {
  TripPlanResponse,
  ItineraryResponse,
  ItineraryItemType,
  StewardGap,
} from "@/lib/types";
import ItineraryItemDialog from "@/components/trips/itinerary/ItineraryItemDialog";
import AddToItinerarySheet, {
  type AddRequest,
} from "@/components/trips/itinerary/AddToItinerarySheet";
import type { AddContext } from "@/components/trips/itinerary/addToItinerary-utils";
import { tripPlaces } from "@/components/trips/itinerary/tripPlaces";
import { planTargetFor } from "@/components/trips/completeness/completeness-plan";
import type { StewardSeed } from "@/components/trips/steward/StewardWorkspace";
import PlanLegSection from "./PlanLegSection";
import BookTripSheet from "./BookTripSheet";
import DidYouBookPrompt from "./DidYouBookPrompt";
import AirfareSummaryRail from "./AirfareSummaryRail";
import TripAdvisorPanel from "./TripAdvisorPanel";
import CompletenessRail from "@/components/trips/completeness/CompletenessRail";
import SourceLegend from "./SourceLegend";
import TravelerNames from "./TravelerNames";
import { invalidateAfterItineraryWrite } from "@/lib/itineraryCache";

/** A deep-link request to auto-open the add dialog (?addStop=1). */
export interface UnifiedAddStopRequest {
  date?: string;
  type?: ItineraryItemType;
}

interface UnifiedItineraryProps {
  tripId: string;
  /** When set (email deep-link), auto-open the add dialog prefilled. */
  addStopRequest?: UnifiedAddStopRequest | null;
  /** Called once the deep-link request has been consumed. */
  onAddStopConsumed?: () => void;
  /**
   * Open the Steward workspace. Called with a seed when the request already has
   * words and a scope (from the add sheet), bare when the traveler just wants
   * to talk.
   */
  onAskSteward?: (seed?: StewardSeed) => void;
  /**
   * Steward's gap analysis, when TripDetail has it. Supplies the DISEMBARK city
   * to the add sheet's place list — on a cruise that city appears on no flight
   * leg, and without it the sheet can only offer the embarkation port.
   */
  stewardGap?: StewardGap | null;
}

/** "2026-09-03" + "2026-09-11" -> "Sep 3 – Sep 11, 2026". Null-safe. */
function formatDateRange(start: string | null, end: string | null): string | null {
  const fmt = (iso: string, withYear: boolean) => {
    const d = new Date(iso + "T00:00:00");
    if (Number.isNaN(d.getTime())) return iso;
    const opts: Intl.DateTimeFormatOptions = withYear
      ? { month: "short", day: "numeric", year: "numeric" }
      : { month: "short", day: "numeric" };
    return d.toLocaleDateString("en-US", opts);
  };
  if (start && end) return fmt(start, false) + " – " + fmt(end, true);
  if (start) return fmt(start, true);
  if (end) return fmt(end, true);
  return null;
}

/** "$2,260" — whole dollars; the itinerary headline never shows cents. */
function money(n: number): string {
  return "$" + Math.round(n).toLocaleString();
}

export default function UnifiedItinerary({
  tripId,
  addStopRequest,
  onAddStopConsumed,
  onAskSteward,
  stewardGap,
}: UnifiedItineraryProps) {
  const queryClient = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery<TripPlanResponse>({
    queryKey: ["tripPlan", tripId],
    queryFn: () => tripPlanApi.get(tripId),
    enabled: !!tripId,
    // The trip plan is the source of truth for dates/fares and changes server-side
    // (date edits, re-searches, the scheduled sweep). Always pull fresh on mount
    // and when the tab regains focus, so the page can never sit on a stale date
    // (e.g. showing a date the route no longer has).
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });

  // Itinerary query supplies dayDates + companions for the add/edit dialog.
  const { data: itinerary } = useQuery<ItineraryResponse>({
    queryKey: ["itinerary", tripId],
    queryFn: () => itineraryApi.get(tripId),
    enabled: !!tripId,
    staleTime: 0,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
  });
  const dayDates = itinerary?.days.map((d) => d.date) ?? [];
  const companions = itinerary?.companions ?? [];

  // Add/edit dialog state.
  const [dialogOpen, setDialogOpen] = useState(false);
  const [bookOpen, setBookOpen] = useState(false);
  /**
   * Which tab the booking sheet opens on.
   *
   * "record" is the MANUAL path (B4/B5): someone who booked last week, or by
   * phone, or who dismissed the prompt, needs the record flow without first
   * pretending to start a handoff.
   */
  const [bookMode, setBookMode] = useState<"go" | "record">("go");
  const [defaultDate, setDefaultDate] = useState<string | undefined>();
  const [defaultType, setDefaultType] = useState<ItineraryItemType | undefined>();
  const [defaultCity, setDefaultCity] = useState<string | undefined>();

  // The picker that now sits in front of the form: what are you adding, and do
  // you want Steward to find it or would you rather type it?
  const [sheetOpen, setSheetOpen] = useState(false);
  const [sheetContext, setSheetContext] = useState<AddContext>({});
  const [sheetChoice, setSheetChoice] = useState<string | null>(null);

  /** Every add affordance on the spine lands here. */
  const openAddSheet = (ctx: AddContext, choiceKey: string | null = null) => {
    setSheetContext(ctx);
    setSheetChoice(choiceKey);
    setSheetOpen(true);
  };

  /** The manual form, prefilled — reached from the sheet, or from a deep link. */
  const openForm = (request: AddRequest) => {
    setDefaultDate(request.date ?? undefined);
    setDefaultCity(request.city ?? undefined);
    setDefaultType(request.itemType);
    setDialogOpen(true);
  };

  /*
   * Deep-link (?addStop=1 from an email) goes STRAIGHT to the form, not to the
   * picker. The link already said what and when; re-asking would be a step
   * backwards from the tap that got them here.
   */
  useEffect(() => {
    if (!addStopRequest) return;
    openForm({
      date: addStopRequest.date ?? null,
      city: null,
      itemType: addStopRequest.type ?? "activity",
    });
    onAddStopConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addStopRequest]);

  if (isLoading) {
    return (
      <div className="space-y-4 rounded-xl border bg-card p-5 shadow-paper">
        <div className="flex items-start justify-between gap-4">
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-28" />
        </div>
        <Skeleton className="h-28 w-full" />
        <Skeleton className="h-28 w-full" />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <div className="flex flex-col items-center gap-3 rounded-xl border border-dashed bg-card py-10 text-center">
        <AlertTriangle className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
        <p className="text-sm text-muted-foreground">We couldn&apos;t load your itinerary.</p>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          Try again
        </Button>
      </div>
    );
  }

  const { trip, legs, total, advisor, sourceLegend, travelers } = data;
  // Where the headline comes from. Older backends don't send it; treat a missing
  // value as "quoted", which is what every response meant before B4.
  const priceBasis = data.priceBasis ?? "quoted";
  const paidTotal = data.paidTotal ?? null;
  const quotedTotal = data.quotedTotal ?? null;

  // Legs not yet booked — the set the trip-level "Book trip" sheet offers.
  const unbookedLegs = legs.filter((l) => (l.bookingStatus ?? "none") === "none");

  /*
   * B5 — legs we handed off to a carrier and still hold no booking for. The
   * server decides (services/bookingPrompt.ts), so the question survives a
   * reload, a closed sheet, or coming back on a different device — and so
   * "Not yet" is remembered rather than re-asked on every render.
   *
   * ⚠️ This never means the leg IS booked. It means we asked them to go
   * somewhere we cannot see, and we owe them the question.
   */
  const legsToAskAbout = unbookedLegs.filter((l) => l.askAboutBooking);

  // Header subline: date range · destination round-trip · N travelers.
  const dateRange = formatDateRange(trip.startDate, trip.endDate);
  const outbound = legs.find((l) => l.role === "outbound") ?? legs[0];
  const destCity = outbound?.destinationCity || outbound?.destination || null;
  const isRoundTrip = legs.some((l) => l.role === "return");
  const tripShape = destCity
    ? destCity + (isRoundTrip ? " round-trip" : " one-way")
    : null;
  const travelerLabel =
    trip.travelers != null
      ? trip.travelers + (trip.travelers === 1 ? " traveler" : " travelers")
      : null;
  const subline = [dateRange, tripShape, travelerLabel].filter(Boolean).join(" · ");

  /*
    R1's acceptance criterion is that the headline EQUALS Σ focus-leg price × travelers —
    so per-person is derived from the same `total` the rail shows, never recomputed from a
    different source. A mismatch between header and legs is the specific failure v1.19.2
    fixed and this must not reintroduce it.
  */
  const pax = trip.travelers && trip.travelers > 0 ? trip.travelers : null;
  const perPerson = total != null && pax ? total / pax : null;

  const returnLeg = legs.find((l) => l.role === "return") ?? null;
  const departAirport = outbound?.origin || null;
  const homeReturnAirport = returnLeg?.destination || outbound?.origin || null;

  if (legs.length === 0) {
    return (
      <div className="rounded-xl border bg-card p-5 shadow-paper">
        <div className="flex flex-col items-center gap-2 py-10 text-center">
          <Plane className="h-7 w-7 text-muted-foreground" aria-hidden="true" />
          <p className="text-sm font-medium">No flights yet</p>
          <p className="text-xs text-muted-foreground">Run a price check to start building your itinerary.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header — dates + travelers (the page <h1> already shows the name). The
          primary "Book trip" action lives in the airfare card (right rail), so
          the figure you'd book and the button to book it sit together. */}
      <div className="rounded-xl border bg-card px-5 py-3 shadow-paper">
        <div className="flex flex-wrap items-start justify-between gap-x-6 gap-y-2">
          <div className="min-w-0 space-y-1">
            {subline && <p className="text-sm text-muted-foreground">{subline}</p>}
            <TravelerNames travelers={travelers} tripId={tripId} passengers={trip.travelers} />
          </div>

          {/*
            R1 — trip total, prominent, ON TOP. Register requirement, and the first
            regression `docs/REQUIREMENTS.md` was written to prevent: past redesigns
            kept demoting this. The airfare rail still carries the breakdown and the
            Book action; this is the headline figure, and it must never be silently
            absent — an unpriced trip says so out loud rather than showing nothing.
            Mono per D4: it is a number, not prose.
          */}
          <div className="shrink-0 text-right">
            {total != null ? (
              <>
                <p className="font-mono text-lg font-medium leading-tight text-foreground">
                  {money(total)} <span className="text-sm font-normal text-muted-foreground">total</span>
                </p>
                {perPerson != null && (
                  <p className="font-mono text-xs text-muted-foreground">
                    {money(perPerson)} per person
                  </p>
                )}
                {/*
                  R1 × B4 × T1 — a paid price and a shopping quote are different
                  facts from different sources, and the header must say which it
                  is showing. On 2026-08-06 this number read $2,398 against a real
                  $3,033 receipt because nothing could record the second one.
                */}
                {priceBasis === "paid" && (
                  <p className="mt-0.5 inline-flex items-center gap-1 text-[11px] font-medium text-emerald-700">
                    <Check className="h-3 w-3" aria-hidden="true" /> What you paid
                  </p>
                )}
                {priceBasis === "mixed" && (
                  <p className="mt-0.5 text-[11px] text-muted-foreground">
                    {paidTotal != null && (
                      <>
                        <span className="font-mono text-emerald-700">{money(paidTotal)}</span> paid
                      </>
                    )}
                    {paidTotal != null && quotedTotal != null && " · "}
                    {quotedTotal != null && (
                      <>
                        <span className="font-mono">{money(quotedTotal)}</span> still a live fare
                      </>
                    )}
                  </p>
                )}
                {priceBasis === "quoted" && (
                  <p className="mt-0.5 text-[11px] text-muted-foreground">Live fare, not booked</p>
                )}
              </>
            ) : (
              <p className="text-sm text-muted-foreground">Pricing your trip…</p>
            )}
          </div>
        </div>
      </div>

      {/* Two-column: itinerary spine (left) + sticky summary rail (right). On
          mobile it collapses to one column with the rail first (airfare on top). */}
      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_320px] lg:items-start">
        {/* Spine */}
        <div className="order-2 space-y-3 lg:order-1">
          {/* Before the first flight — the slot that was missing entirely.
              It now opens the picker rather than a blank transfer form: the
              person asking "how do I get to SEA" is exactly the person who
              should be offered a car, a shuttle and a park-and-fly. */}
          {departAirport && (
            <SpineAddSlot
              label={"Before you fly · get to " + departAirport}
              onClick={() =>
                openAddSheet({
                  date: outbound?.date ?? null,
                  city: outbound?.originCity ?? null,
                  position: "before your flight out of " + departAirport,
                })
              }
            />
          )}

          {legs.map((leg) => (
            <PlanLegSection
              key={leg.routeId}
              tripId={tripId}
              leg={leg}
              passengers={trip.travelers}
              travelers={travelers}
              onAddStop={({ date, city }) =>
                openAddSheet({
                  date,
                  city,
                  position: city ? "while you're in " + city : null,
                })
              }
            />
          ))}

          {/* After the last flight — get home. */}
          {homeReturnAirport && (
            <SpineAddSlot
              label={"After you land · get home from " + homeReturnAirport}
              onClick={() =>
                openAddSheet({
                  date: returnLeg?.date ?? outbound?.date ?? null,
                  city: null,
                  position: "after you land at " + homeReturnAirport,
                })
              }
            />
          )}

          {/* The spine's Steward affordance: for the traveler who knows a day
              is empty but not yet what belongs in it. Distinct from the add
              slots above — those need you to already know the answer. */}
          {onAskSteward && (
            <button
              type="button"
              onClick={() => onAskSteward()}
              className="flex w-full items-center gap-2 rounded-lg border border-dashed border-[hsl(var(--primary))]/40 bg-card px-3 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:bg-[hsl(var(--primary))]/[0.05] motion-reduce:transition-none"
            >
              <Sparkles
                className="h-4 w-4 shrink-0 text-[hsl(var(--primary))]"
                aria-hidden="true"
              />
              <span className="flex-1">Not sure what to add? Plan it with Steward</span>
              <span className="text-xs text-[hsl(var(--primary))]">Ask</span>
            </button>
          )}

          <SourceLegend entries={sourceLegend ?? []} />
        </div>

        {/* Rail */}
        <aside className="order-1 space-y-4 lg:sticky lg:top-4 lg:order-2 lg:self-start">
          {/* B5 — "did you book it?", surviving whatever the traveller did after
              the handoff. Above the airfare figure because it is the reason that
              figure might be wrong. */}
          {legsToAskAbout.length > 0 && (
            <DidYouBookPrompt
              tripId={tripId}
              legs={legsToAskAbout}
              onRecord={() => { setBookMode("record"); setBookOpen(true); }}
            />
          )}

          {/* Airfare figure + the primary Book-trip action, one container. */}
          {(total != null || unbookedLegs.length > 0) && (
            <div className="rounded-xl border bg-card p-4 shadow-paper">
              <AirfareSummaryRail
                airfare={total}
                breakdown={data.costBreakdown}
                travelers={trip.travelers}
                priceBasis={priceBasis}
                bare
              />
              {unbookedLegs.length > 0 && (
                <div className={total != null ? "mt-3 border-t pt-3" : ""}>
                  <Button
                    className="w-full"
                    onClick={() => { setBookMode("go"); setBookOpen(true); }}
                  >
                    <Ticket className="mr-1.5 h-4 w-4" aria-hidden="true" />
                    Book trip
                    {unbookedLegs.length > 1 && (
                      <>
                        {" · "}
                        <span className="font-mono">{unbookedLegs.length}</span> flights
                      </>
                    )}
                  </Button>
                  {/* The MANUAL door into the record flow. Always here — booked
                      by phone, booked last week, or the prompt was dismissed. */}
                  <button
                    type="button"
                    onClick={() => { setBookMode("record"); setBookOpen(true); }}
                    className="mt-2 w-full text-center text-xs font-medium text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
                  >
                    Already booked? Record what you paid
                  </button>
                </div>
              )}
            </div>
          )}
          {advisor && (
            <div className="rounded-xl border bg-card p-4 shadow-paper">
              <TripAdvisorPanel advisor={advisor} />
            </div>
          )}
          {/* The gap list now OPENS the add flow instead of running its own
              parallel questionnaire — see completeness-plan.ts. */}
          <CompletenessRail
            tripId={tripId}
            itinerary={itinerary}
            onPlan={(el) => {
              const t = planTargetFor(el, data);
              openAddSheet(t.context, t.choiceKey);
            }}
          />
        </aside>
      </div>

      {/* The picker in front of the form — what, then how. */}
      <AddToItinerarySheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        context={sheetContext}
        dayDates={dayDates}
        defaultChoiceKey={sheetChoice}
        places={tripPlaces({
          slotCity: sheetContext.city,
          plan: data,
          itinerary,
          gap: stewardGap,
        })}
        onAskSteward={(seed) => onAskSteward?.(seed)}
        onEnterManually={openForm}
      />

      {/* Shared add/edit dialog */}
      <ItineraryItemDialog
        tripId={tripId}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={null}
        dayDates={dayDates}
        defaultDate={defaultDate}
        defaultType={defaultType}
        defaultCity={defaultCity}
        companions={companions}
        onSaved={() => invalidateAfterItineraryWrite(queryClient, tripId)}
      />

      {/* Trip-level booking sheet (primary "Book trip" action). */}
      <BookTripSheet
        open={bookOpen}
        onOpenChange={setBookOpen}
        tripId={tripId}
        legs={unbookedLegs}
        passengers={trip.travelers}
        travelers={travelers}
        initialMode={bookMode}
      />
    </div>
  );
}

/** A dashed "add" slot for the spine — used before the first flight and after the
 *  last, where ground transport (get to / from the airport) belongs. */
function SpineAddSlot({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-2 rounded-lg border border-dashed border-border bg-card px-3 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:bg-muted/40"
    >
      <Plus className="h-4 w-4 shrink-0" aria-hidden="true" />
      <span className="flex-1">{label}</span>
      <span className="text-xs text-[hsl(var(--primary))]">Add</span>
    </button>
  );
}
